import { FC, useState } from 'react';
import { Controller, UseFormSetValue } from 'react-hook-form';
import { Switch } from '@headlessui/react';
import { ReactComponent as TriangleIcon } from 'assets/icons/triangle.svg';
import ReactTooltip from 'react-tooltip';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { createPortal } from 'react-dom';

interface AllowDoctorSelectionSectionProps {
  control: any;
  setValue: UseFormSetValue<any>;
  isPreferredPractitionerEnabled: boolean;
}

const AllowDoctorSelectionSection: FC<AllowDoctorSelectionSectionProps> = ({
  control,
  setValue,
  isPreferredPractitionerEnabled,
}) => {
  const [isExapanded, setIsExapanded] = useState(true);

  return (
    <div className="flex flex-col gap-y-1.4 text-14 leading-[2.1rem]">
      <button
        className="flex items-start gap-x-0.8"
        onClick={() => setIsExapanded((isExapanded) => !isExapanded)}
      >
        <TriangleIcon
          className={`${
            isExapanded ? '' : '-rotate-90'
          } transition-transform w-1.6 h-1.6`}
        />
        <span className="font-bold text-left text-16 leading-[2.2rem] text-darkest-grey">
          Practitioner Selection
        </span>
      </button>
      {isExapanded && (
        <div className="pl-1 -pr-2 -mr-2 flex flex-col gap-y-2">
          <section>
            <div className="flex flex-col gap-y-1.4 text-15 leading-[2.1rem]">
              <button className="font-bold text-left text-14 leading-[2.2rem] text-darkest-grey">
                Allow patient to choose their preferred practitioner
                <InfoIcon
                  data-for="preferred-practitioner-info"
                  data-tip=""
                  className="w-1.6 h-1.6 all-child:fill-mid-grey inline-block ml-0.4 cursor-pointer"
                />
              </button>
              {createPortal(
                <ReactTooltip
                  id="preferred-practitioner-info"
                  effect="solid"
                  place="bottom"
                  className="!bg-white !rounded-[0.8rem] !w-[18.5rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
                >
                  <span className="text-11 text-darkest-grey flex items-center font-normal text-left">
                    Turning off this option will also prevent patients from
                    selecting their preferred doctor for exam or cleaning
                    services.
                  </span>
                </ReactTooltip>,
                document.body
              )}
            </div>
            <Controller
              control={control}
              name="isPreferredPractitionerEnabled"
              render={({ field }) => {
                const { value } = field;
                return (
                  <div className="space-y-2.1">
                    <div className="flex gap-x-1.6 items-center mt-1">
                      <Switch
                        {...field}
                        onChange={(newValue: boolean) => {
                          if (newValue === false) {
                            setValue('isDoctorSelectionEnabled', false, {
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                          }
                          field.onChange(newValue);
                        }}
                        checked={value}
                        className={`${
                          value ? 'bg-magenta' : 'bg-[#78788029]'
                        } relative inline-flex h-3.1 w-5.1 items-center rounded-full`}
                      >
                        <span
                          className={`${
                            value ? 'translate-x-[2.3rem]' : 'translate-x-[2px]'
                          } inline-block h-2.5 w-2.5 transform rounded-full transition bg-white`}
                        />
                      </Switch>
                      <span className="uppercase">{value ? 'On' : 'Off'}</span>
                    </div>
                  </div>
                );
              }}
            />
          </section>
          <section>
            <div className="flex flex-col gap-y-1.4 text-15 leading-[2.1rem]">
              <span className="font-bold text-left text-14 leading-[2.2rem] text-darkest-grey">
                Allow Patients to Select Doctor for Exam/Cleaning
              </span>
            </div>
            <Controller
              control={control}
              name="isDoctorSelectionEnabled"
              render={({ field }) => {
                const { value } = field;

                return (
                  <div className="space-y-2.1">
                    <div className="flex gap-x-1.6 items-center mt-1">
                      <Switch
                        {...field}
                        checked={value}
                        className={`${
                          value ? 'bg-magenta' : 'bg-[#78788029]'
                        } relative inline-flex h-3.1 w-5.1 items-center rounded-full disabled:opacity-50 disabled:cursor-not-allowed`}
                        disabled={isPreferredPractitionerEnabled === false}
                      >
                        <span
                          className={`${
                            value ? 'translate-x-[2.3rem]' : 'translate-x-[2px]'
                          } inline-block h-2.5 w-2.5 transform rounded-full transition bg-white`}
                        />
                      </Switch>
                      <span className="uppercase">{value ? 'On' : 'Off'}</span>
                    </div>
                  </div>
                );
              }}
            />
          </section>
        </div>
      )}
    </div>
  );
};

export default AllowDoctorSelectionSection;
